<script setup lang="ts">
/**
 * @changelog
 *
 * 2026-03-11 (0.1.0) (jhoward)
 *   - Add MaterialFormContainer.vue as a proxy for framework form container.
 *
 * 2026-03-24 (0.1.1) (jhoward)
 *   - Use getRouteWithFixedUrl helper to normalize route URLs before passing to material-form-container.
 *
 * 2026-03-30 (0.1.2) (jhoward)
 *   - Change default for closeCurrentSnackOnSubmit from true to false.
 */

import type {
  FetchResponseFormSubmission,
  MaterialFormContainerStoreActions,
  MaterialFormContainerStoreState,
} from '@framework/components/material/MaterialFormContainer.vue';
import MaterialFormContainer from '@framework/components/material/MaterialFormContainer.vue';
import type { Method, RouteDefinition } from '@framework/defs/wayfinder';
import type { peppermillCustomFetch } from '@framework/helpers';
import type { StoreDefinition } from 'pinia';
import type { PropType } from 'vue';
import type { Router } from 'vue-router';
import getRouteWithFixedUrl from '@/helpers/getRouteWithFixedUrl';
import peppermillCustomFetchProxy from '@/helpers/peppermillCustomFetchProxy';
import localRouter from '@/router';
import useAppStore from '@/store';

const props = defineProps({
  action: {
    type: String,
    default: './',
  },
  enctype: {
    type: String as PropType<'application/x-www-form-urlencoded' | 'multipart/form-data'>,
    default: 'application/x-www-form-urlencoded',
  },
  method: {
    type: String as PropType<Method>,
    default: 'POST',
  },
  route: {
    type: Object as PropType<RouteDefinition<Method> | null>,
    default: null,
  },
  skipValidation: {
    type: Boolean,
    default: false,
  },
  hideSteps: {
    type: Boolean,
    default: false,
  },
  submitValidationCallback: {
    type: Function as PropType<() => boolean>,
    default: () => true,
  },
  submitResponseCallback: {
    type: Function as PropType<(this: HTMLFormElement, response: FetchResponseFormSubmission) => void>,
    default: undefined,
  },
  ignoreAjax: {
    type: Boolean,
    default: false,
  },
  closeCurrentSnackOnSubmit: {
    type: Boolean,
    default: false,
  },
  useAppStore: {
    type: Function as PropType<
      StoreDefinition<'app', MaterialFormContainerStoreState, {}, MaterialFormContainerStoreActions>
    >,
    default: useAppStore,
  },
  router: {
    type: Object as PropType<Router | null>,
    default: null,
  },
  fetchHelper: {
    type: Function as PropType<typeof peppermillCustomFetch>,
    default: peppermillCustomFetchProxy,
  },
});

const routeFixed = props.route ? getRouteWithFixedUrl(props.route) : null;
// console.log('routeFixed', routeFixed);
</script>

<template>
  <material-form-container
    :action="action"
    :enctype="enctype"
    :method="method"
    :route="routeFixed"
    :skip-validation="skipValidation"
    :hide-steps="hideSteps"
    :submit-validation-callback="submitValidationCallback"
    :submit-response-callback="submitResponseCallback"
    :ignore-ajax="ignoreAjax"
    :close-current-snack-on-submit="closeCurrentSnackOnSubmit"
    :use-app-store="useAppStore"
    :router="router || localRouter"
    :fetch-helper="fetchHelper"
  >
    <slot></slot>
  </material-form-container>
</template>

<style scoped></style>
